/* ==========================================================================
   Bedrock — Chiffres clés (compteur animé)
   Vanilla, sans dépendance. Custom element <bdr-impact-numbers>.
   ========================================================================== */

(function () {
  'use strict';

  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Courbe d'accélération douce (ease-out cubic). */
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function decimalsOf(value) {
    const str = String(value);
    const dot = str.indexOf('.');
    return dot === -1 ? 0 : str.length - dot - 1;
  }

  function formatNumber(value, decimals) {
    const lang = document.documentElement.getAttribute('lang') || undefined;
    try {
      return new Intl.NumberFormat(lang, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    } catch (error) {
      return value.toFixed(decimals);
    }
  }

  class BdrImpactNumbers extends HTMLElement {
    connectedCallback() {
      this.duration = parseInt(this.dataset.duration, 10) || 1800;
      this.counters = Array.from(this.querySelectorAll('[data-bdr-counter]'));
      this.animatedCounters = new WeakSet();
      this.rafIds = [];

      if (!this.counters.length) return;

      if (reduceMotion() || !('IntersectionObserver' in window)) {
        this.counters.forEach((counter) => this.setFinal(counter));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.animate(entry.target);
            this.observer.unobserve(entry.target);
          });
        },
        { threshold: 0.35 }
      );

      this.counters.forEach((counter) => this.observer.observe(counter));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.rafIds.forEach((id) => cancelAnimationFrame(id));
      this.rafIds = [];
    }

    setFinal(counter) {
      const target = parseFloat(counter.dataset.target) || 0;
      const decimals = decimalsOf(counter.dataset.target);
      const numberEl = counter.querySelector('[data-bdr-number]');
      if (numberEl) numberEl.textContent = formatNumber(target, decimals);
    }

    animate(counter) {
      if (this.animatedCounters.has(counter)) return;
      this.animatedCounters.add(counter);

      const target = parseFloat(counter.dataset.target) || 0;
      const decimals = decimalsOf(counter.dataset.target);
      const numberEl = counter.querySelector('[data-bdr-number]');
      if (!numberEl) return;

      const start = performance.now();
      const duration = this.duration;

      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);
        const current = target * eased;

        numberEl.textContent = formatNumber(current, decimals);

        if (progress < 1) {
          const id = requestAnimationFrame(step);
          this.rafIds.push(id);
        } else {
          numberEl.textContent = formatNumber(target, decimals);
        }
      };

      const id = requestAnimationFrame(step);
      this.rafIds.push(id);
    }
  }

  if (!customElements.get('bdr-impact-numbers')) {
    customElements.define('bdr-impact-numbers', BdrImpactNumbers);
  }

  /* Le theme editor remplace le HTML de la section au complet lors d'une
     modification de réglage : le custom element est démonté puis remonté,
     ce qui relance naturellement connectedCallback / disconnectedCallback
     ci-dessus. Rien de plus à faire ici pour shopify:section:load. */
})();
